import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { DataRoomShareableLink } from 'models/ShareableLink';
import bus, { StandardEventTypes } from 'modules/bus';
import { SUGGESTED_ACTION_TYPE } from 'router/subrouters/DataRoom/models/Dashboard';
import ProlongShareableLinkModal from 'router/subrouters/DataRoom/subrouters/ShareableLinks/pages/ShareableLinks/components/ProlongShareableLinkModal';
import { removeActiveSuggestedAction } from '../utils';

type ProlongModalData = {
  shareableLinkId: string;
  linkExpiresDate: Date;
};

export type OpenProlongModal = (
  shareableLinkId: string,
  linkExpiresDate: Date,
) => void;

const useProlongSecureLink = (
  onSuccess?: (shareableLink: Partial<DataRoomShareableLink>) => void,
) => {
  const [prolongModal, prolongModalRef] = useCallbackRef<ModalRef>(null);
  const [prolongModalData, setProlongModalData] =
    useState<ProlongModalData | null>(null);

  const queryClient = useQueryClient();

  const removeActiveSuggestion = useCallback(
    (shareableLink: Partial<DataRoomShareableLink>) => {
      onSuccess?.(shareableLink);

      bus.broadcastEvent(
        StandardEventTypes.SHAREABLE_LINK_UPDATED,
        shareableLink,
      );

      removeActiveSuggestedAction(queryClient, [
        SUGGESTED_ACTION_TYPE.SECURE_LINKS_EXPIRING,
      ]);
    },
    [queryClient, onSuccess],
  );

  const openProlongModal = useCallback<OpenProlongModal>(
    (shareableLinkId, linkExpiresDate) => {
      setProlongModalData({
        shareableLinkId,
        linkExpiresDate,
      });
      prolongModal?.open();
    },
    [prolongModal],
  );

  const closeProlongModal = useCallback(() => {
    setProlongModalData(null);
  }, []);

  const prolongSecureLinkModal = useMemo(
    () => (
      <ProlongShareableLinkModal
        ref={prolongModalRef}
        shareableLinkId={prolongModalData?.shareableLinkId}
        linkExpiresDate={prolongModalData?.linkExpiresDate}
        onSuccess={removeActiveSuggestion}
        onClose={closeProlongModal}
      />
    ),
    [
      prolongModalData,
      closeProlongModal,
      prolongModalRef,
      removeActiveSuggestion,
    ],
  );

  return {
    prolongSecureLinkModal,
    openProlongModal,
  };
};

export default useProlongSecureLink;
